import { Component, Input, OnInit } from '@angular/core';
import { AbstractModalComponent } from "../abstract-modal/abstract-modal.component";
import { BsModalRef } from "ngx-bootstrap/modal";
import { WarningModalService } from "../../services/warning-modal/warning-modal.service";

// Mapping between session_age (minutes) and display labels
const SESSION_AGE_TO_LABEL: { [key: number]: string } = {
  60: '1 hour',
  180: '3 hours',
  480: '8 hours',
  1440: '24 hours',
  2880: '48 hours'
};

const LABEL_TO_SESSION_AGE: { [key: string]: number } = {
  '1 hour': 60,
  '3 hours': 180,
  '8 hours': 480,
  '24 hours': 1440,
  '48 hours': 2880
};

@Component({
  selector: 'app-password-authentication-policies-modal',
  templateUrl: './password-authentication-policies-modal.component.pug',
  styleUrls: ['./password-authentication-policies-modal.component.scss'],
  standalone: false
})
export class PasswordAuthenticationPoliciesModalComponent extends AbstractModalComponent implements OnInit {

  @Input() apiPasswordPolicy: any = null;
  @Input() readOnly: boolean = false;

  saving = false;

  private initialPasswordPolicy: any = {};
  private initialAuthenticationPolicy: any = {};

  passwordPolicy = {
    requireUppercase: true,
    requireLowercase: true,
    requireSpecialChar: true,
    requireNumber: true,
    minLength: 12
  };

  authenticationPolicy = {
    limitPasswordReset: false,
    requirePasswordChangeAfter: 'never', // 'never', '30', '60', '90'
    previousPasswordsReuse: 6, // 6, 9, 12
    sessionExpireAfter: '8 hours'
  };

  minLengthOptions = [
    { key: '10', value: '10' },
    { key: '11', value: '11' },
    { key: '12', value: '12' },
    { key: '13', value: '13' },
    { key: '14', value: '14' },
    { key: '15', value: '15' },
    { key: '16', value: '16' },
    { key: '17', value: '17' },
    { key: '18', value: '18' },
    { key: '19', value: '19' },
    { key: '20', value: '20' }
  ];

  sessionExpireOptions = [
    { key: '1 hour', value: $localize`1 hour` },
    { key: '3 hours', value: $localize`3 hours` },
    { key: '8 hours', value: $localize`8 hours` },
    { key: '24 hours', value: $localize`24 hours` },
    { key: '48 hours', value: $localize`48 hours` }
  ];

  passwordChangeOptions = [
    { key: 'never', label: $localize`Never` },
    { key: '30', label: $localize`30 days` },
    { key: '60', label: $localize`60 days` },
    { key: '90', label: $localize`90 days` }
  ];

  previousPasswordsOptions = [
    { key: '6', label: '6' },
    { key: '9', label: '9' },
    { key: '12', label: '12' }
  ];

  checkboxLabels = {
    uppercase: $localize`At least 1 uppercase character`,
    lowercase: $localize`At least 1 lowercase character`,
    specialChar: $localize`At least 1 special character`,
    number: $localize`At least 1 number`
  };

  limitPasswordResetLabel = $localize`Limit users to one password reset per day.`;

  constructor(public _bsModalRef: BsModalRef, public modal: WarningModalService) {
    super(_bsModalRef, modal);
  }

  ngOnInit(): void {
    if (this.apiPasswordPolicy) {
      this.mapApiToUi(this.apiPasswordPolicy);
    }
    this.snapshotInitialState();
  }

  /**
   * Maps the API password policy structure to the UI model.
   */
  mapApiToUi(policy: any): void {
    if (!policy) return;

    const rules = policy.complex_password_rules ?? {};
    this.passwordPolicy.requireUppercase = rules.force_upper_case ?? true;
    this.passwordPolicy.requireLowercase = rules.force_lower_case ?? true;
    this.passwordPolicy.requireSpecialChar = rules.force_special_chars ?? true;
    this.passwordPolicy.requireNumber = rules.force_numbers ?? true;
    this.passwordPolicy.minLength = policy.password_minimum_length ?? 12;

    this.authenticationPolicy.limitPasswordReset = policy.password_changes_per_day === 1;
    this.authenticationPolicy.previousPasswordsReuse = policy.previous_passwords_to_remember ?? 6;

    // Map password_expiry to radio option
    if (policy.password_expiry === null || policy.password_expiry === undefined) {
      this.authenticationPolicy.requirePasswordChangeAfter = 'never';
    } else {
      this.authenticationPolicy.requirePasswordChangeAfter = String(policy.password_expiry);
    }

    // Map session_age (minutes) to display label
    const sessionAge = policy.session_age ?? 480;
    this.authenticationPolicy.sessionExpireAfter = SESSION_AGE_TO_LABEL[sessionAge] ?? '8 hours';
  }

  /**
   * Converts the UI model back to the API password policy structure.
   */
  mapUiToApi(): any {
    return {
      session_age: LABEL_TO_SESSION_AGE[this.authenticationPolicy.sessionExpireAfter] ?? 480,
      password_expiry: this.authenticationPolicy.requirePasswordChangeAfter === 'never'
        ? null
        : parseInt(this.authenticationPolicy.requirePasswordChangeAfter, 10),
      complex_password_rules: {
        force_numbers: this.passwordPolicy.requireNumber,
        force_lower_case: this.passwordPolicy.requireLowercase,
        force_upper_case: this.passwordPolicy.requireUppercase,
        force_special_chars: this.passwordPolicy.requireSpecialChar
      },
      password_minimum_length: this.passwordPolicy.minLength,
      password_changes_per_day: this.authenticationPolicy.limitPasswordReset ? 1 : null,
      previous_passwords_to_remember: this.authenticationPolicy.previousPasswordsReuse,
      inactive_account_suspension_trigger: this.apiPasswordPolicy?.inactive_account_suspension_trigger ?? null
    };
  }

  cancel() {
    if (this.readOnly) {
      this._bsModalRef.hide();
      return;
    }
    this.canDeactivate().subscribe(response => {
      if (response) {
        this.markAsPristine();
        this._bsModalRef.hide();
      }
    });
  }

  save() {
    if (this.readOnly) {
      return;
    }
    this.saving = true;
    this.messages = [];
    const apiPayload = this.mapUiToApi();
    this.event.emit(apiPayload);
  }

  getForm() {
    return {
      dirty: this.formDirty,
      form: {
        markAsPristine: () => { this.formDirty = false; }
      }
    };
  }

  onPasswordPolicyChange(field: string, value: boolean) {
    if (this.readOnly) {
      return;
    }
    (this.passwordPolicy as any)[field] = value;
    this.checkDirty();
  }

  onMinLengthChange(option: any) {
    if (this.readOnly) {
      return;
    }
    this.passwordPolicy.minLength = parseInt(option.key, 10);
    this.checkDirty();
  }

  onLimitPasswordResetChange(checked: boolean) {
    if (this.readOnly) {
      return;
    }
    this.authenticationPolicy.limitPasswordReset = checked;
    this.checkDirty();
  }

  onPasswordChangeAfterChange(option: any) {
    if (this.readOnly) {
      return;
    }
    this.authenticationPolicy.requirePasswordChangeAfter = option.key;
    this.checkDirty();
  }

  onPreviousPasswordsReuseChange(option: any) {
    if (this.readOnly) {
      return;
    }
    this.authenticationPolicy.previousPasswordsReuse = parseInt(option.key, 10);
    this.checkDirty();
  }

  onSessionExpireChange(option: any) {
    if (this.readOnly) {
      return;
    }
    this.authenticationPolicy.sessionExpireAfter = option.key;
    this.checkDirty();
  }

  private snapshotInitialState(): void {
    this.initialPasswordPolicy = { ...this.passwordPolicy };
    this.initialAuthenticationPolicy = { ...this.authenticationPolicy };
  }

  private checkDirty(): void {
    this.formDirty =
      this.passwordPolicy.requireUppercase !== this.initialPasswordPolicy.requireUppercase ||
      this.passwordPolicy.requireLowercase !== this.initialPasswordPolicy.requireLowercase ||
      this.passwordPolicy.requireSpecialChar !== this.initialPasswordPolicy.requireSpecialChar ||
      this.passwordPolicy.requireNumber !== this.initialPasswordPolicy.requireNumber ||
      this.passwordPolicy.minLength !== this.initialPasswordPolicy.minLength ||
      this.authenticationPolicy.limitPasswordReset !== this.initialAuthenticationPolicy.limitPasswordReset ||
      this.authenticationPolicy.requirePasswordChangeAfter !== this.initialAuthenticationPolicy.requirePasswordChangeAfter ||
      this.authenticationPolicy.previousPasswordsReuse !== this.initialAuthenticationPolicy.previousPasswordsReuse ||
      this.authenticationPolicy.sessionExpireAfter !== this.initialAuthenticationPolicy.sessionExpireAfter;
  }

  closeModal() {
    this.cancel();
  }

  getSelectedMinLengthLabel(): string {
    const selected = this.minLengthOptions.find(opt => opt.key === this.passwordPolicy.minLength.toString());
    return selected ? selected.value : '12';
  }

  getSelectedSessionExpireLabel(): string {
    const selected = this.sessionExpireOptions.find(opt => opt.key === this.authenticationPolicy.sessionExpireAfter);
    return selected ? selected.value : $localize`8 hours`;
  }
}
